import type { Components, Theme } from '@mui/material/styles';

/**
 * Outlined field fill — matches MUI Paper/Dialog (`background.paper`) so search bars
 * don't sit on a darker plane than the dialog in dark mode.
 */
export function outlinedInputFill(theme: Theme): string {
  return theme.palette.background.paper;
}

/** Shared outlined field surface styles. */
export function outlinedInputComponentOverrides(): Components<Theme>['MuiOutlinedInput'] {
  return {
    styleOverrides: {
      root: ({ theme }) => {
        const fill = outlinedInputFill(theme);
        return {
          backgroundColor: fill,
          backgroundImage: 'none',
          color: theme.palette.text.primary,
          '&:hover': {
            backgroundColor: fill,
            backgroundImage: 'none',
          },
          '&.Mui-focused': {
            backgroundColor: fill,
            backgroundImage: 'none',
          },
          '&.Mui-disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
            color: theme.palette.text.disabled,
            '& .MuiOutlinedInput-input': {
              WebkitTextFillColor: theme.palette.text.disabled,
            },
          },
          '& .MuiOutlinedInput-input': {
            color: theme.palette.text.primary,
            WebkitTextFillColor: theme.palette.text.primary,
            '&::placeholder': {
              color: theme.palette.mode === 'dark' ? theme.palette.grey[500] : theme.palette.text.secondary,
              WebkitTextFillColor: theme.palette.mode === 'dark' ? theme.palette.grey[500] : theme.palette.text.secondary,
              opacity: 1,
            },
          },
          '& input:-webkit-autofill': {
            WebkitBoxShadow: `0 0 0 100px ${fill} inset`,
            WebkitTextFillColor: theme.palette.text.primary,
            caretColor: theme.palette.text.primary,
          },
        };
      },
      input: {
        backgroundColor: 'transparent',
      },
    },
  };
}

export function textFieldComponentOverrides(): Components<Theme>['MuiTextField'] {
  return {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 8,
        },
      },
    },
  };
}

export function selectComponentOverrides(
  base?: Components<Theme>['MuiSelect'],
): Components<Theme>['MuiSelect'] {
  return {
    ...base,
    styleOverrides: {
      ...base?.styleOverrides,
      root: {
        borderRadius: 8,
      },
      select: {
        backgroundColor: 'transparent',
        '&:focus': {
          backgroundColor: 'transparent',
        },
      },
    },
  };
}

export function getInputComponentOverrides(
  baseSelect?: Components<Theme>['MuiSelect'],
): Pick<Components<Theme>, 'MuiOutlinedInput' | 'MuiTextField' | 'MuiSelect'> {
  return {
    MuiOutlinedInput: outlinedInputComponentOverrides(),
    MuiTextField: textFieldComponentOverrides(),
    MuiSelect: selectComponentOverrides(baseSelect),
  };
}
